import { useCallback, useEffect, useRef, useState } from 'react'

type Setter<T> = (next: T | ((prev: T) => T)) => void

/**
 * Everything currently reading a given key, so a write reaches all of them.
 *
 * Without this each hook instance held a private copy that it read from storage
 * once, on mount. Two surfaces sharing a key would then disagree for as long as
 * both stayed mounted: the team map's view settings are read by the Map tab's
 * inline map AND by the full-screen and print surfaces that open on top of it, so
 * changing the border treatment in the print surface updated that map, wrote the
 * new value to storage, and left the map underneath rendering the old one until it
 * happened to remount.
 */
const listeners = new Map<string, Set<() => void>>()

function subscribe(key: string, listener: () => void): () => void {
  const forKey = listeners.get(key) ?? new Set<() => void>()
  listeners.set(key, forKey)
  forKey.add(listener)
  return () => {
    forKey.delete(listener)
    if (forKey.size === 0) listeners.delete(key)
  }
}

function read<T>(key: string, initial: T): T {
  if (typeof window === 'undefined') return initial
  try {
    const raw = window.localStorage.getItem(key)
    if (raw == null) return initial
    return JSON.parse(raw) as T
  } catch {
    return initial
  }
}

export function useLocalStorage<T>(key: string, initial: T): [T, Setter<T>] {
  const [value, setValue] = useState<T>(() => read(key, initial))

  // Mirrors the latest value so `update` can resolve a functional update without
  // going through setState — it has to write to storage and notify peers
  // synchronously, and a setState updater runs later (and may run twice).
  const valueRef = useRef(value)
  valueRef.current = value

  // Identifies this instance's own listener so a write does not make the writer
  // re-read what it just set.
  const selfRef = useRef<(() => void) | null>(null)

  // Re-read on any write to this key — from another hook instance on this page, or
  // from another tab via the `storage` event. Reading back from storage rather than
  // passing the value along keeps one source of truth, so both paths behave the same.
  useEffect(() => {
    const resync = () => setValue(read(key, initial))
    selfRef.current = resync
    const unsubscribe = subscribe(key, resync)
    const onStorage = (e: StorageEvent) => {
      // `key === null` is a whole-store clear.
      if (e.key === key || e.key === null) resync()
    }
    window.addEventListener('storage', onStorage)
    // Catch a write that landed between this instance's first render and here.
    resync()
    return () => {
      unsubscribe()
      selfRef.current = null
      window.removeEventListener('storage', onStorage)
    }
    // `initial` is only the fallback for a missing entry. Depending on it would
    // resubscribe — and reset every peer — whenever a caller passed a fresh object
    // literal, which is the normal way to call this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const update = useCallback<Setter<T>>(
    (next) => {
      const resolved =
        typeof next === 'function' ? (next as (p: T) => T)(valueRef.current) : next
      try {
        window.localStorage.setItem(key, JSON.stringify(resolved))
      } catch {
        // localStorage unavailable (privacy mode etc.) — keep state in memory.
      }
      valueRef.current = resolved
      setValue(resolved)
      // Peers re-read from storage, which now holds `resolved`.
      for (const listener of listeners.get(key) ?? []) {
        if (listener !== selfRef.current) listener()
      }
    },
    [key],
  )

  return [value, update]
}
